import { useContestAttemptVerification, useContestAttemptVerifications } from '../contestsQueries.ts';
import { useEffect, useState } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
    IContestAttemptAnswerDto,
    IContestQuestionAnswerDto,
} from '../models/dto/IContestAttemptVerificationResponse.ts';
import ReactPlayer from 'react-player';
import { ButtonSize, ButtonType, ButtonVariant } from '../../../types/button.ts';
import Button from '../../buttons/Button.tsx';
import { useModal } from '../../../hooks/useModal.tsx';
import apiNet from '../../../newApi/apiNet.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PulseLoader } from '../../loaders/PulseLoader.tsx';
import { ContestAttemptStatusBadge } from './ContestAttemptStatusBadge.tsx';
import { useNotification } from '../../../providers/NotificationProvider.tsx';
import { NotificationStatus } from '../../notification/Notification.tsx';
import { ContestQuestionType } from '../../../types/account/marketing/contestQuestionTypeTypes.ts';
import CircleLoader from '../../loaders/CircleLoader.tsx';
import { ContestAttemptStatusType } from '../../../types/account/marketing/contestAttemptStatusTypeTypes.ts';

export interface IProps {
    contestId: string;
    attemptId?: string;
}

const ContestVerifyAttemptModalContent = ({ contestId, attemptId }: IProps) => {
    const { closeModal } = useModal();
    const { showNotification } = useNotification();
    const queryClient = useQueryClient();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [verificationItems, setVerificationItems] = useState({
        questions: {} as Record<string, number>,
        videos: {} as Record<string, number>,
    });

    const isSingleMode = attemptId != null;
    const result = useContestAttemptVerifications(contestId, !isSingleMode);
    const ids = result.data?.data ?? [];
    const currentId = isSingleMode ? attemptId : ids[currentIndex];

    const detailQuery = useContestAttemptVerification(contestId, currentId);

    const allVideoScored = detailQuery.data?.data.videos.every((e) => verificationItems.videos[e.id] != null);
    const allTextQuestionsScored = detailQuery.data?.data.questions.filter((e) => e.question.type === ContestQuestionType.TEXT).every((e) => verificationItems.questions[e.id] != null);

    const canSubmit = allVideoScored && allTextQuestionsScored;

    const getAnswerBorderClass = (userAnswer: IContestAttemptAnswerDto | null, answer: IContestQuestionAnswerDto): string => {
        if (answer.isCorrect) {
            if (userAnswer?.selectedAnswerIds.some(value => value === answer.id)) {
                return 'border-green-500 bg-green-500/5 text-green-500';
            } else {
                return 'border-blue-500 bg-blue-500/5 text-blue-500';
            }
        } else {
            if (userAnswer?.selectedAnswerIds.some(value => value === answer.id)) {
                return 'border-red-500  bg-red-500/5 text-red-500';
            } else {
                return 'border-neutral-300/70';
            }
        }
    };

    const updateVerification = (
        id: string,
        score: number,
        type: 'video' | 'question',
    ) => {
        const key = type === 'video' ? 'videos' : 'questions';

        setVerificationItems(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [id]: score,
            },
        }));
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            showNotification('Niepowodzenie', 'Musisz zaznaczyć liczbę punktów dla wideo oraz wszystkich pytań otwartych przed zatwierdzeniem oceny.', NotificationStatus.failure);
            return;
        }

        await contestAttemptVerificationMutation.mutateAsync();
    };

    const contestAttemptVerificationMutation = useMutation({
        mutationFn: () =>
            apiNet.postContestAttemptVerification(contestId, currentId, {
                videos: Object.entries(verificationItems.videos).map(([id, score]) => ({
                    id,
                    score,
                })),
                questions: Object.entries(verificationItems.questions).map(([id, score]) => ({
                    id,
                    score,
                })),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['contests', contestId, 'attempts', 'verifications'],
                exact: true,
            });
            // setCurrentIndex((prev) => Math.min(prev + 1, Math.max(ids.length - 1, 0)));
        },
    });


    useEffect(() => {
        if (ids.length === 0 && result.isSuccess) {
            closeModal();
        }
        if (currentIndex >= ids.length && ids.length > 0) {
            setCurrentIndex(ids.length - 1);
        }
    }, [ids.length, result]);

    useEffect(() => {
        const videos = detailQuery.data?.data.videos;

        if (!videos) return;

        setVerificationItems(prev => ({
            ...prev,
            videos: Object.fromEntries(
                videos.map(item => [
                    item.id,
                    prev.videos[item.id] ?? item.score,
                ]),
            ),
        }));
    }, [detailQuery.data?.data.videos]);

    if (detailQuery.isLoading) {
        return <PulseLoader />;
    }

    return (
        detailQuery.isSuccess && detailQuery.data.data &&
        <div
            className={'bg-scroll h-full overflow-auto p-3 space-y-4 animate-opacityEaseIn flex flex-col justify-between'}>
            <section className={'space-y-4'}>
                <div className={'rounded-2xl bg-neutral-800 p-3 grid grid-cols-[70px_1fr] gap-y-1.5'}>
                    <span className="font-medium">Id:</span>
                    <span>{detailQuery.data?.data.id}</span>
                    <span className="font-medium">Punkty:</span>
                    <span>{detailQuery.data?.data.score}/{detailQuery.data?.data.maxScore}</span>
                    <span className="font-medium">Status:</span>
                    <ContestAttemptStatusBadge
                        status={detailQuery.data.data.status} />
                </div>

                {detailQuery.data?.data.videos.length > 0 &&
                    <div>
                        <p className={'text-xl ml-3'}>Wideo</p>
                        {detailQuery.data?.data.videos.map((item) => <div
                                key={item.id}
                                className={'rounded-2xl bg-neutral-800 p-3 space-y-1.5 mt-2'}>
                                <div className={'bg-neutral-950 rounded-2xl overflow-hidden w-full aspect-video'}>
                                    <ReactPlayer width={'100%'} height={'100%'} controls
                                                 url={import.meta.env.VITE_MIX_API_URL + item.path.replace('/api', '')} />
                                </div>
                                <div className={'mt-3'}>
                                    <p>Ilość punktów</p>
                                    <div className={'flex space-x-2 mt-2'}>
                                        {
                                            Array.from({ length: detailQuery.data.data.maxVideoPoints + 1 }, (_, i) => (
                                                <button key={i}
                                                        disabled={detailQuery.data.data.status !== ContestAttemptStatusType.PENDING_REVIEW}
                                                        onClick={() => {
                                                            updateVerification(item.id, i, 'video');
                                                        }}
                                                        className={`rounded-full border size-8 flex items-center justify-center hover:bg-neutral-950 transition enabled:cursor-pointer ${verificationItems.videos[item.id] === i ? 'border-blue-500 text-blue-500 bg-blue-500/10' : ''}`}>{i}</button>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>,
                        )}
                    </div>
                }
                <div className={'bg-neutral-800 p-3  rounded-2xl'}>
                    <Disclosure>
                        <DisclosureButton className="group flex w-full items-center justify-between cursor-pointer">
                    <span className="text-lg/6 font-medium text-white group-data-hover:text-white/80">
                        Odpowiedzi na pytania
                    </span>
                            <ChevronDownIcon
                                className="size-5 fill-white/60 group-data-hover:fill-white/50 group-data-open:rotate-180" />
                        </DisclosureButton> <DisclosurePanel className="text-gray-500">
                        {detailQuery.data?.data.questions.map((question) =>
                            <div className={'mt-6'} key={question.id}>
                                <p className={'text-neutral-200'}>{question.question.content}</p>
                                {question.question.answers.map((answer) =>
                                    <div key={answer.id}
                                         className={`border rounded-xl p-3 mt-2 ${getAnswerBorderClass(question.answer, answer)}`}>{answer.content}</div>,
                                )}
                            </div>,
                        )}
                    </DisclosurePanel>
                    </Disclosure>
                </div>
            </section>
            <div className={'flex gap-4'}>
                <Button
                    type={ButtonType.SUBMIT}
                    size={ButtonSize.LARGE}
                    variant={ButtonVariant.PRIMARY}
                    disabled={contestAttemptVerificationMutation.isPending || detailQuery.data.data.status !== ContestAttemptStatusType.PENDING_REVIEW}
                    className="flex justify-center items-center mt-1 px-10 py-1.5 grow shrink-0"
                    onClick={async () => await handleSubmit()}
                >
                    {contestAttemptVerificationMutation.isPending &&
                        <CircleLoader className={'h-6 w-6 mr-2'} />
                    }
                    Zatwierdź
                </Button>
                <Button
                    type={ButtonType.SUBMIT}
                    size={ButtonSize.LARGE}
                    variant={ButtonVariant.TERTIARY}
                    // disabled={currentIndex == ids.length-1}
                    className={'flex justify-center items-center mt-1 px-10 py-2 grow shrink-0'}
                    onClick={() => {
                        {
                            (isSingleMode || currentIndex == ids.length - 1) ? closeModal() : setCurrentIndex(prevState => prevState + 1);
                        }
                    }}
                >{(isSingleMode || currentIndex == ids.length - 1) ? 'Zamknij' : 'Następny'}</Button>
            </div>
        </div>
    );
};

export default ContestVerifyAttemptModalContent;